import i18n from '@yandex-int/i18n';
import * as languagesKeyset from 'i18n/Language';
import * as skillCreationKeyset from 'i18n/SkillCreation';
import merge from 'lodash/merge';
import pickBy from 'lodash/pickBy';

import {
    MultilingualSetupEntry,
    SkillAccess,
    SmartHomeBackendSettings,
    SmartHomePublishingSettings,
    SmartHomeSettings,
} from '../../model/settings';
import { LanguageCode, SmartHomeFormSettings, SupportedLanguageFormData } from '../../types';
import { isPrivateSkillsEnabled } from '..';

import { getParameterWithDefault } from './common';

const tSkillCreation = i18n(skillCreationKeyset);
const tLanguage = i18n(languagesKeyset);

const skipUndefined = (obj: object) => pickBy(obj, (x) => x !== undefined);

const getInitialValues: () => SmartHomeFormSettings = () => {
    return {
        brandVerificationWebsite: '',
        logoId: '',
        logoUrl: '',
        name: tSkillCreation('smart-home-skill-default-name'),
        voice: 'shitova.us',
        developerName: '',
        category: 'smart_home',
        noteForModerator: '',
        description: '',
        backendType: 'webhook',
        uri: '',
        functionId: '',
        email: '',
        oauthAppId: '',
        androidDeepLink: '',
        iosDeepLink: '',
        iosDeepLinkFallbackURL: '',
        secondaryTitle: '',
        isTrustedSmartHomeSkill: false,
        skillAccess: SkillAccess.Public,
        hideInStore: false,
        supportedLanguages: [
            {
                description: '',
                subtitle: '',
                appName: tSkillCreation('smart-home-skill-default-name'),
            },
        ],
        enableAllAvailableRegions: true,
        selectedRegions: [],
        skillUserAgreements: [],
    };
};

export function mapValuesToSmartHomeSettings(values: SmartHomeFormSettings): Partial<SmartHomeSettings> {
    const supportedLanguagesToMultilingualSettings: { [key: string]: MultilingualSetupEntry } = {};

    values.supportedLanguages.forEach((language) => {
        if (language.languageCode !== undefined) {
            supportedLanguagesToMultilingualSettings[language.languageCode] = {
                name: language.appName,
                secondaryTitle: language.subtitle,
                externalSettingsDescription: language.externalSettingsDescription,
                supportedUnitsDescription: language.supportedUnitsDescription,
                supportServiceUrl: language.supportServiceUrl || undefined,
            } as MultilingualSetupEntry;
        }
    });

    const fallbackLanguageFormEntry = values.supportedLanguages[0];

    const enableAllAvailableRegions = values.enableAllAvailableRegions;
    const multilingualSettings =
        Object.keys(supportedLanguagesToMultilingualSettings).length === 0
            ? undefined
            : supportedLanguagesToMultilingualSettings;

    return skipUndefined({
        logo2: null,

        name: fallbackLanguageFormEntry?.appName,
        voice: values.voice,
        logoId: values.logoId,
        skillAccess: values.skillAccess,
        hideInStore: values.hideInStore,
        noteForModerator: values.noteForModerator,

        backendSettings: {
            uri: values.backendType === 'webhook' ? values.uri : '',
            functionId: values.backendType === 'function' ? values.functionId : '',
            backendType: values.backendType,
        },

        publishingSettings: skipUndefined({
            brandVerificationWebsite: values.brandVerificationWebsite,
            category: values.category,
            developerName: values.developerName,
            description: fallbackLanguageFormEntry?.description,
            secondaryTitle: fallbackLanguageFormEntry?.subtitle,
            email: values.email,
            smartHome: {
                deepLinks: {
                    android: {
                        url: values.androidDeepLink,
                    },
                    ios: {
                        url: values.iosDeepLink,
                        fallbackUrl: values.iosDeepLinkFallbackURL,
                    },
                },
            },
            multilingualSettings,
        }),

        oauthAppId: values.oauthAppId,
        isTrustedSmartHomeSkill: values.isTrustedSmartHomeSkill,
        enableAllAvailableRegions,
        selectedRegions: values.selectedRegions,
    });
}

export function mapSmartHomeSettingsToValues(skillSettings: SmartHomeSettings): SmartHomeFormSettings {
    const initialValues = getInitialValues();
    const { brandVerificationWebsite, category, developerName, description, email, multilingualSettings } = {
        ...initialValues,
        ...skillSettings.publishingSettings,
    } as Required<SmartHomePublishingSettings>;

    const { uri, functionId, backendType } = {
        ...initialValues,
        ...skillSettings.backendSettings,
    } as Required<SmartHomeBackendSettings>;

    let deepLinks = {
        ios: {
            url: '',
            fallbackUrl: '',
        },
        android: {
            url: '',
        },
    };
    if (skillSettings.publishingSettings.smartHome) {
        deepLinks = merge(deepLinks, skillSettings.publishingSettings.smartHome.deepLinks);
    }

    const displaySkillAccess = isPrivateSkillsEnabled();

    let supportedLanguagesFromMultilingualSettings: SupportedLanguageFormData[] = [];

    if (multilingualSettings !== undefined && typeof multilingualSettings === 'object') {
        supportedLanguagesFromMultilingualSettings = (Object.keys(multilingualSettings) as LanguageCode[])
            .filter((value) => value in languagesKeyset.en)
            .map(
                (languageCode) =>
                    ({
                        languageCode: languageCode,
                        languageDisplayName: tLanguage(languageCode),
                        appName: multilingualSettings[languageCode]?.name || '',
                        description: multilingualSettings[languageCode]?.description,
                        subtitle: multilingualSettings[languageCode]?.secondaryTitle || '',
                        externalSettingsDescription:
                            multilingualSettings[languageCode]?.externalSettingsDescription || '',
                        supportedUnitsDescription: multilingualSettings[languageCode]?.supportedUnitsDescription,
                        supportServiceUrl: multilingualSettings[languageCode]?.supportServiceUrl,
                    }) as SupportedLanguageFormData,
            );
    }

    // old skill that is migrating to a multilingual skill
    if (supportedLanguagesFromMultilingualSettings.length === 0) {
        supportedLanguagesFromMultilingualSettings = [
            {
                appName: skillSettings.name || initialValues?.supportedLanguages[0]?.appName,
                subtitle:
                    skillSettings.publishingSettings.secondaryTitle || initialValues?.supportedLanguages[0]?.subtitle,
                externalSettingsDescription: description || initialValues?.supportedLanguages[0]?.description,
            },
        ];
    }

    return {
        brandVerificationWebsite,
        developerName,
        category,
        description,
        secondaryTitle: skillSettings.publishingSettings.secondaryTitle || '',
        uri,
        email,
        functionId,
        backendType,

        androidDeepLink: deepLinks.android.url,
        iosDeepLink: deepLinks.ios.url,
        iosDeepLinkFallbackURL: deepLinks.ios.fallbackUrl,

        name: getParameterWithDefault(skillSettings, initialValues, 'name'),
        logoId: getParameterWithDefault(skillSettings, initialValues, 'logoId'),
        logoUrl: skillSettings?.logo2?.url || '',
        noteForModerator: getParameterWithDefault(skillSettings, initialValues, 'noteForModerator'),
        voice: getParameterWithDefault(skillSettings, initialValues, 'voice'),
        isTrustedSmartHomeSkill: getParameterWithDefault(skillSettings, initialValues, 'isTrustedSmartHomeSkill'),
        oauthAppId: getParameterWithDefault(skillSettings, initialValues, 'oauthAppId'),
        skillAccess: displaySkillAccess
            ? getParameterWithDefault(skillSettings, initialValues, 'skillAccess')
            : undefined,
        hideInStore: getParameterWithDefault(skillSettings, initialValues, 'hideInStore'),
        supportedLanguages: supportedLanguagesFromMultilingualSettings,
        enableAllAvailableRegions: getParameterWithDefault(skillSettings, initialValues, 'enableAllAvailableRegions'),
        selectedRegions: getParameterWithDefault(skillSettings, initialValues, 'selectedRegions'),
        skillUserAgreements: getParameterWithDefault(skillSettings, initialValues, 'skillUserAgreements'),
    };
}
